/**
 * Marquee scrolling text element
 * Creates infinite horizontal scrolling animation with configurable speed and pause on hover
 *
 * The animation only runs while the marquee is on screen, un-paused and the tab is visible.
 * An ungated loop writes a transform to a composited, full-width layer every frame for the
 * life of the page, which on iOS accumulates paint pressure until the tab is killed.
 */

const VIEWPORT_MARGIN = '200px'

export class MarqueeElement extends HTMLElement {
  private animationId: number | null = null
  private scrollPosition: number = 0
  private content: HTMLElement | null = null
  private autoplaySpeed: number = 3000
  private isPaused: boolean = false
  private isOnScreen: boolean = false
  private contentWidth: number = 0
  private resizeObserver: ResizeObserver | null = null
  private intersectionObserver: IntersectionObserver | null = null
  private widthTimer: number | null = null
  private originalChildren: Node[] = []
  private isSetup: boolean = false

  private handleMouseEnter = () => this.pause()
  private handleMouseLeave = () => this.resume()
  private handleVisibilityChange = () => this.syncAnimationState()

  connectedCallback() {
    this.setupMarquee()
    this.setupIntersectionObserver()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.syncAnimationState()
  }

  disconnectedCallback() {
    this.stopAnimation()

    if (this.widthTimer !== null) {
      window.clearTimeout(this.widthTimer)
      this.widthTimer = null
    }

    this.resizeObserver?.disconnect()
    this.resizeObserver = null

    this.intersectionObserver?.disconnect()
    this.intersectionObserver = null

    this.isOnScreen = false

    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.removeEventListener('mouseenter', this.handleMouseEnter)
    this.removeEventListener('mouseleave', this.handleMouseLeave)
  }

  private setupMarquee() {
    // Get autoplay speed from data attribute
    const speedAttr = this.getAttribute('data-autoplay-speed')
    if (speedAttr) {
      this.autoplaySpeed = parseInt(speedAttr, 10) || 3000
    }

    if (this.isSetup && this.content?.isConnected) {
      this.setupResizeObserver()
      return
    }

    // Store original children
    this.originalChildren = Array.from(this.childNodes)

    // Create content wrapper that will hold original + cloned content
    this.content = document.createElement('div')
    this.content.style.cssText = `
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      min-width: max-content;
    `

    // Move original children to content wrapper
    this.originalChildren.forEach(child => {
      this.content!.appendChild(child)
    })

    // Clone the content for seamless loop
    this.originalChildren.forEach(child => {
      const clone = child.cloneNode(true)
      this.content!.appendChild(clone)
    })

    // Add content to element
    this.appendChild(this.content)

    // Setup resize observer to recalculate content width when content changes
    this.setupResizeObserver()

    // Calculate initial content width after a brief delay to ensure DOM is rendered
    this.widthTimer = window.setTimeout(() => {
      this.widthTimer = null
      this.calculateContentWidth()
    }, 0)

    // Pause on hover
    this.addEventListener('mouseenter', this.handleMouseEnter)
    this.addEventListener('mouseleave', this.handleMouseLeave)

    this.isSetup = true
  }

  private setupResizeObserver() {
    if (!('ResizeObserver' in window) || !this.content) return

    this.resizeObserver?.disconnect()
    this.resizeObserver = new ResizeObserver(() => {
      this.calculateContentWidth()
    })
    this.resizeObserver.observe(this.content)
  }

  private setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      this.isOnScreen = true
      return
    }

    this.intersectionObserver?.disconnect()
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.isOnScreen = entries.some((entry) => entry.isIntersecting)
        this.syncAnimationState()
      },
      { rootMargin: VIEWPORT_MARGIN }
    )
    this.intersectionObserver.observe(this)
  }

  private calculateContentWidth() {
    if (!this.content || this.originalChildren.length === 0) return

    const width = this.content.getBoundingClientRect().width / 2
    if (width <= 0) return

    this.contentWidth = width

    if (this.scrollPosition >= this.contentWidth) {
      this.scrollPosition = this.scrollPosition % this.contentWidth
    }
  }

  /**
   * Single source of truth for whether the loop should be running.
   */
  private syncAnimationState() {
    const shouldAnimate = this.isOnScreen && !this.isPaused && !document.hidden

    if (shouldAnimate) this.startAnimation()
    else this.stopAnimation()
  }

  private startAnimation() {
    if (this.animationId !== null) return

    if (this.content) {
      this.content.style.willChange = 'transform'
    }

    const animate = () => {
      if (this.content && this.contentWidth > 0) {
        // Calculate speed based on autoplay speed (lower number = faster)
        const speedMultiplier = Math.max(0.2, 3000 / this.autoplaySpeed)
        this.scrollPosition += speedMultiplier

        if (this.scrollPosition >= this.contentWidth) {
          this.scrollPosition -= this.contentWidth
        }

        this.content.style.transform = `translateX(-${this.scrollPosition}px)`
      }

      this.animationId = requestAnimationFrame(animate)
    }

    this.animationId = requestAnimationFrame(animate)
  }

  private stopAnimation() {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }

    if (this.content) {
      this.content.style.willChange = 'auto'
    }
  }

  private pause() {
    this.isPaused = true
    this.syncAnimationState()
  }

  private resume() {
    this.isPaused = false
    this.syncAnimationState()
  }

  public pauseMarquee() {
    this.pause()
  }

  public resumeMarquee() {
    this.resume()
  }

  public setSpeed(speed: number) {
    this.autoplaySpeed = speed
  }

  public recalculateWidth() {
    this.calculateContentWidth()
  }
}
